import React, { useState, useEffect, useMemo } from 'react';
import { FiX, FiClock } from 'react-icons/fi';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import TdsFields, { EMPTY_TDS } from './TdsFields';
import { resolveTds, allocationsPayload, tdsPayload } from '../../utils/tds';

/**
 * EditReceiptModal — edit a saved payment (ClientReceipt).
 * Used by /admin/billing (All Receipts) and /admin/payments (Payment History).
 *
 * Props
 *   receiptId : number   – receipt to edit
 *   onClose   : () => void
 *   onSaved   : (receipt) => void   – called after a successful save
 *
 * Bill receipts (R…)   : date, mode, account, note, cash amount, credit used and
 *                        how the money is split across the client's bills.
 * Order receipts (RS…) : date, mode, account, note and amount.
 * Receipt number / client are fixed. Server validates everything again.
 */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v) || 0;
const r2  = (v) => Math.round(num(v) * 100) / 100;
const fmtDT = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const labelSt = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputSt = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff' };

const MODES = [
  { v: 'cash', l: 'Cash' },
  { v: 'upi', l: 'UPI' },
  { v: 'cheque', l: 'Cheque' },
  { v: 'bank_transfer', l: 'Bank Transfer' },
  { v: 'other', l: 'Other' },
];

const accountMatchesMode = (a, mode) => {
  if (mode === 'cash') return a.account_type === 'cash';
  if (mode === 'upi')  return a.account_type === 'upi';
  if (mode === 'bank_transfer' || mode === 'cheque') return a.account_type === 'bank';
  return true;
};

const FIELD_LABELS = {
  receiptDate: 'Date', mode: 'Mode', bankAccountLabel: 'Account', note: 'Note',
  totalAmount: 'Cash', creditUsed: 'Credit used', surplusCredit: 'Wallet credit',
  tdsAmount: 'TDS', tdsSection: 'TDS section',
};

const diffText = (before = {}, after = {}) => {
  const out = [];
  Object.keys(FIELD_LABELS).forEach((k) => {
    const b = before[k] ?? '', a = after[k] ?? '';
    if (String(b) !== String(a)) {
      const isMoney = ['totalAmount', 'creditUsed', 'surplusCredit', 'tdsAmount'].includes(k);
      out.push(`${FIELD_LABELS[k]}: ${isMoney ? money(b) : (b || '—')} → ${isMoney ? money(a) : (a || '—')}`);
    }
  });
  const bs = JSON.stringify((before.billAllocations || []).map((x) => [x.billId, num(x.allocatedAmount), num(x.tdsAmount)]));
  const as = JSON.stringify((after.billAllocations || []).map((x) => [x.billId, num(x.allocatedAmount), num(x.tdsAmount)]));
  if (bs !== as) {
    const f = (l) => (l || []).map((x) => `${x.billNumber} ${money(num(x.allocatedAmount) + num(x.tdsAmount))}${num(x.tdsAmount) > 0 ? ` (incl. TDS ${money(x.tdsAmount)})` : ''}`).join(', ') || '—';
    out.push(`Bills: ${f(before.billAllocations)} → ${f(after.billAllocations)}`);
  }
  return out;
};

const EditReceiptModal = ({ receiptId, onClose, onSaved }) => {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [receipt, setReceipt]   = useState(null);
  const [order, setOrder]       = useState(null);
  const [rows, setRows]         = useState([]);      // bill rows [{billId, billNumber, balance, amount}]
  const [wallet, setWallet]     = useState(0);
  const [history, setHistory]   = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showSplit, setShowSplit] = useState(false);   // split table is hidden for single-bill receipts until asked
  const [banks, setBanks]       = useState([]);
  const [form, setForm]         = useState({ date: '', mode: 'cash', bankAccountId: '', note: '', total: '', credit: '' });
  const [tdsInput, setTdsInput] = useState(EMPTY_TDS);   // TDS % / amount / section (bill receipts)
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const isBill = !!receipt && Array.isArray(receipt.billAllocations) && receipt.billAllocations.length > 0;
  // Receipt that pays exactly one bill → simple mode: the amount just goes to that bill.
  const singleBillId = isBill && receipt.billAllocations.length === 1 ? Number(receipt.billAllocations[0].billId) : null;
  const simple = singleBillId != null && !showSplit;

  /* ── load everything ── */
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [{ data }, hist, bank] = await Promise.all([
          API.get(`/client-receipts/${receiptId}`),
          API.get(`/client-receipts/${receiptId}/history`).catch(() => ({ data: { history: [] } })),
          API.get('/company-bank-accounts', { params: { active: 'true' } }).catch(() => ({ data: { accounts: [] } })),
        ]);
        if (dead) return;
        const r = data.receipt;
        setReceipt(r);
        setOrder(data.order || null);
        setHistory(hist.data.history || []);
        setBanks(bank.data.accounts || []);
        setForm({
          date: String(r.receiptDate || '').slice(0, 10),
          mode: r.mode || 'cash',
          bankAccountId: r.bankAccountId ? String(r.bankAccountId) : '',
          note: r.note || '',
          total: String(num(r.totalAmount)),
          credit: String(num(r.creditUsed)),
        });

        if (num(r.tdsAmount) > 0) {
          setTdsInput({ edited: 'amount', percent: '', amount: String(num(r.tdsAmount)), section: r.tdsSection || '' });
        }

        const alloc = r.billAllocations || [];
        if (alloc.length) {
          // bills of this client that can take money (+ the ones already on this receipt)
          let open = [];
          if (r.businessId) {
            const o = await API.get(`/client-receipts/open-bills/${r.businessId}`).catch(() => ({ data: { bills: [] } }));
            open = o.data.bills || [];
          }
          const byId = new Map();
          (data.bills || []).forEach((b) => byId.set(b.id, b));
          open.forEach((b) => byId.set(b.id, b));
          // what this receipt settles on each bill = cash/credit part + TDS part
          const mine = new Map(alloc.map((a) => [Number(a.billId), r2(num(a.allocatedAmount) + num(a.tdsAmount))]));
          const list = Array.from(byId.values()).map((b) => {
            const own = mine.get(b.id) || 0;
            return {
              bill: b,
              billId: b.id,
              billNumber: b.billNumber,
              billDate: b.billDate,
              grandTotal: num(b.grandTotal),
              // balance as if THIS receipt were not applied
              balance: r2(num(b.grandTotal) - num(b.amountReceived) + own),
              amount: own ? String(own) : '',
            };
          }).sort((a, b) => (mine.has(b.billId) ? 1 : 0) - (mine.has(a.billId) ? 1 : 0));
          setRows(list);
        }
        if (r.businessId) {
          API.get(`/client-receipts/credit/${r.businessId}`)
            .then(({ data: c }) => !dead && setWallet(num(c.creditBalance))).catch(() => {});
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load receipt');
        onClose();
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [receiptId]); // eslint-disable-line

  const cash   = num(form.total);
  const credit = isBill ? num(form.credit) : num(receipt?.creditUsed);
  const effective = r2(cash + credit);
  const singleRow = simple ? rows.find((r) => r.billId === singleBillId) : null;

  // TDS: % and amount are linked (utils/tds.js). Balances are "as if this receipt were not applied".
  const balanceById = new Map(rows.map((r) => [r.billId, r.balance]));
  const planRows = simple ? (singleRow ? [singleRow] : []) : rows;
  const resolved = resolveTds({
    bills: planRows.map((r) => r.bill),
    effective,
    mode: simple ? 'fifo' : 'manual',
    manualSettle: Object.fromEntries(rows.map((r) => [r.billId, num(r.amount)])),
    balanceOf: (b) => balanceById.get(b.id) ?? 0,
    excludeRn: receipt?.receiptNumber || null,
    input: isBill ? tdsInput : EMPTY_TDS,
  });
  const tdsAmt   = resolved.amount;
  const toSettle = r2(effective + tdsAmt);                 // cash + credit + TDS to knock off the bills
  // simple mode: the one bill takes what it can, any extra cash goes to the client's wallet
  // split mode : sum of the boxes (each box = cash + TDS settled on that bill)
  const settleSum = simple
    ? resolved.plan.totalSettle
    : r2(rows.reduce((s, r) => s + num(r.amount), 0));
  const allocSum  = settleSum;
  const surplus   = isBill ? (simple ? resolved.plan.surplus : r2(toSettle - settleSum)) : 0;

  // order receipt: how much can it be at most
  const orderMax = useMemo(() => {
    if (isBill || !order || !receipt) return null;
    return r2(num(order.grandTotal) - num(order.amountReceived) + num(receipt.totalAmount));
  }, [isBill, order, receipt]);

  const openSplit = () => {
    setRows((prev) => prev.map((r) => ({ ...r, amount: r.billId === singleBillId ? String(allocSum || '') : '' })));
    setShowSplit(true);
  };

  const autoSplit = () => {
    // oldest bill first, but keep the rows where they are on screen
    const order = [...rows].sort((a, b) => String(a.billDate || '').localeCompare(String(b.billDate || '')) || a.billId - b.billId);
    let left = toSettle;
    const take = new Map();
    order.forEach((r) => {
      const t = Math.max(0, Math.min(left, r.balance));
      left = r2(left - t);
      take.set(r.billId, t);
    });
    setRows((prev) => prev.map((r) => ({ ...r, amount: take.get(r.billId) > 0 ? String(take.get(r.billId)) : '' })));
  };

  const problems = [];
  if (!loading && receipt) {
    if (isBill) {
      if (effective <= 0 && tdsAmt <= 0) problems.push('Enter a payment amount or the TDS deducted');
      if (resolved.error) problems.push(resolved.error);
      if (simple) {
        if (!singleRow) problems.push('Bill details could not be loaded — use "Change bills"');
      } else {
        rows.forEach((r) => { if (num(r.amount) > r.balance + 0.01) problems.push(`${r.billNumber}: more than its balance (${money(r.balance)})`); });
        if (allocSum <= 0) problems.push('Allocate the payment to at least one bill');
      }
      if (surplus < -0.01) problems.push(`Allocated ${money(allocSum)} is more than the payment ${money(toSettle)}${tdsAmt > 0 ? ' (incl. TDS)' : ''}`);
    } else {
      if (cash <= 0) problems.push('Enter a payment amount');
      if (orderMax != null && cash > orderMax + 0.01) problems.push(`Amount is more than the order balance (${money(orderMax)})`);
    }
    if (!form.date) problems.push('Choose a date');
  }

  // the TDS error is already shown inside the TDS box — keep it blocking Save, but don't repeat it down here
  const shownProblems = problems.filter((p) => p !== resolved.error);

  const handleSave = async () => {
    if (problems.length) { toast.error(problems[0]); return; }
    const payload = {
      receiptDate: form.date,
      mode: form.mode,
      bankAccountId: form.bankAccountId || null,
      bankAccountLabel: banks.find((a) => String(a.id) === String(form.bankAccountId))?.account_name
        || (String(receipt.bankAccountId || '') === String(form.bankAccountId) ? receipt.bankAccountLabel : null),
      note: form.note,
      totalAmount: cash,
    };
    if (isBill) {
      payload.creditUsed = credit;
      payload.billAllocations = allocationsPayload({
        rows: resolved.plan.rows.filter((r) => r.settle > 0),
      });
      // always sent (0 clears TDS); % is only sent when the user typed the %, otherwise the server derives it
      Object.assign(payload, tdsPayload(resolved, tdsInput, tdsInput.section));
    }
    try {
      setSaving(true);
      const { data } = await API.put(`/client-receipts/${receiptId}`, payload);
      toast.success(data.message || 'Receipt updated');
      if (isBill && surplus > 0.01) toast(`${money(surplus)} will sit in the client's credit wallet`, { icon: '💰' });
      onSaved && onSaved(data.receipt);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update receipt');
    } finally { setSaving(false); }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const card = { background: '#fff', borderRadius: 14, padding: 24, width: 680, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' };

  if (loading || !receipt) {
    return (
      <div style={overlay}><div style={{ ...card, width: 320, textAlign: 'center', color: '#6b7280' }}>Loading receipt…</div></div>
    );
  }

  return (
    <div style={overlay}>
      <div style={card}>
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
              Edit Receipt <span style={{ color: '#6366f1' }}>{receipt.receiptNumber}</span>
            </h3>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>
              {receipt.companyName || receipt.customerName}
              <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: isBill ? '#eef2ff' : '#fef3c7', color: isBill ? '#4f46e5' : '#b45309' }}>
                {isBill ? 'Bill receipt' : `Order receipt${order ? ` · ${order.orderNumber}` : ''}`}
              </span>
              {history.length > 0 && (
                <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#f3f4f6', color: '#4b5563' }}>
                  Edited {history.length}×
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>

        {/* basic fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelSt}>Date</label>
            <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()} style={{ ...inputSt, cursor: 'pointer' }} />
          </div>
          <div>
            <label style={labelSt}>Mode</label>
            <select value={form.mode} onChange={(e) => { set('mode', e.target.value); set('bankAccountId', ''); }} style={inputSt}>
              {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelSt}>Received in account</label>
            <select value={form.bankAccountId} onChange={(e) => set('bankAccountId', e.target.value)} style={inputSt}>
              <option value="">— Select Account —</option>
              {/* keep the saved account selectable even if it is inactive now */}
              {receipt.bankAccountId && !banks.some((a) => String(a.id) === String(receipt.bankAccountId)) && (
                <option value={String(receipt.bankAccountId)}>{receipt.bankAccountLabel || `Account #${receipt.bankAccountId}`}</option>
              )}
              {banks.filter((a) => accountMatchesMode(a, form.mode)).map((a) => (
                <option key={a.id} value={a.id}>{a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelSt}>Note</label>
            <input type="text" value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Optional" style={inputSt} />
          </div>
          <div>
            <label style={labelSt}>Cash received (₹)</label>
            <input type="number" min="0" step="0.01" value={form.total} onChange={(e) => set('total', e.target.value)} style={inputSt} />
            {!isBill && orderMax != null && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Max {money(orderMax)}</div>}
          </div>
          {isBill && (
            <div>
              <label style={labelSt}>Credit wallet used (₹)</label>
              <input type="number" min="0" step="0.01" value={form.credit} onChange={(e) => set('credit', e.target.value)} style={inputSt} />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Wallet now: {money(wallet)}</div>
            </div>
          )}
        </div>

        {/* TDS deducted by the customer (bill receipts only) */}
        {isBill && (
          <div style={{ marginTop: 14 }}>
            <TdsFields input={tdsInput} onChange={setTdsInput} resolved={resolved} overflowMode="wallet" />
          </div>
        )}

        {/* bill split */}
        {isBill && simple && (
          <div style={{ marginTop: 18, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>
              Applied to bill <strong>{receipt.billAllocations[0].billNumber}</strong>
              {singleRow && <span style={{ color: '#6b7280' }}> · can take {money(singleRow.balance)}</span>}
              <span style={{ color: '#16a34a', marginLeft: 8, fontWeight: 600 }}>{money(allocSum)}</span>
              {tdsAmt > 0 && <span style={{ color: '#b45309', marginLeft: 6 }}>(cash {money(resolved.plan.cashAllocated)} + TDS {money(tdsAmt)})</span>}
              {surplus > 0.01 && (
                <span style={{ color: '#d97706', marginLeft: 8, fontWeight: 600 }}>
                  + {money(surplus)} extra → client's credit wallet
                </span>
              )}
            </span>
            <button type="button" onClick={openSplit}
              style={{ background: 'none', border: 'none', color: '#6366f1', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 }}>
              Change bills
            </button>
          </div>
        )}

        {isBill && !simple && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Split across bills</div>
              <button type="button" onClick={autoSplit}
                style={{ padding: '5px 10px', border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Auto-split oldest first
              </button>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Bill', 'Bill total', 'Can take', tdsAmt > 0 ? 'Settled by this receipt (₹, incl. TDS)' : 'This receipt (₹)'].map((h, i) => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: i === 0 ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.billId} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{r.billNumber}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>{money(r.grandTotal)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>{money(r.balance)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <input type="number" min="0" step="0.01" max={r.balance} value={r.amount}
                          onChange={(e) => setRows((prev) => prev.map((x) => (x.billId === r.billId ? { ...x, amount: e.target.value } : x)))}
                          style={{ ...inputSt, width: 120, textAlign: 'right', padding: '6px 8px', borderColor: num(r.amount) > r.balance + 0.01 ? '#f87171' : '#e5e7eb' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8, padding: '9px 12px', background: '#f9fafb', borderRadius: 8, fontSize: 13, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <span>Payment: <strong>{money(toSettle)}</strong>{tdsAmt > 0 && <span style={{ color: '#b45309' }}> (incl. TDS {money(tdsAmt)})</span>}</span>
              <span>Allocated: <strong>{money(allocSum)}</strong></span>
              <span style={{ color: surplus < -0.01 ? '#dc2626' : surplus > 0.01 ? '#d97706' : '#16a34a' }}>
                {surplus < -0.01 ? `Over by ${money(-surplus)}` : surplus > 0.01 ? `To wallet: ${money(surplus)}` : 'Fully allocated'}
              </span>
            </div>
          </div>
        )}

        {shownProblems.length > 0 && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#b91c1c' }}>
            {shownProblems.map((p) => <div key={p}>• {p}</div>)}
          </div>
        )}

        {/* history */}
        {history.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <button type="button" onClick={() => setShowHistory((s) => !s)}
              style={{ background: 'none', border: 'none', color: '#6366f1', fontWeight: 600, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, padding: 0 }}>
              <FiClock size={12} /> {showHistory ? 'Hide' : 'Show'} edit history
            </button>
            {showHistory && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {history.map((h) => {
                  const m = typeof h.meta === 'string' ? (() => { try { return JSON.parse(h.meta); } catch { return {}; } })() : (h.meta || {});
                  const lines = diffText(m.before, m.after);
                  return (
                    <div key={h.id} style={{ border: '1px solid #f3f4f6', borderRadius: 8, padding: '8px 10px', fontSize: 12, background: '#fafafa' }}>
                      <div style={{ fontWeight: 600, color: '#374151' }}>{fmtDT(h.createdAt)} · {m.editedBy || 'Admin'}</div>
                      {lines.length ? lines.map((l) => <div key={l} style={{ color: '#6b7280' }}>{l}</div>) : <div style={{ color: '#9ca3af' }}>No visible change</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* footer */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || problems.length > 0}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--primary, #6366f1)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving || problems.length ? 'not-allowed' : 'pointer', opacity: saving || problems.length ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditReceiptModal;